/**
 * Share-link minting wizard.
 *
 * Replaces the legacy single-screen mint form with a question-driven
 * flow that mirrors how Frank/Arooj/Nicki actually send a proposal:
 *
 *   1. Cognito proposal number
 *   2. Did the client provide their own cost grid? → if yes, upload
 *   3. Prepare link (mints + uploads attachments)
 *   4. Preview the link
 *   5. Confirm happy with the preview
 *   6. Choose the next move:
 *        a. Reply to the client's email   → steps 7–9
 *        b. Just give me the link          → step 7
 *   7a. Upload the client's .msg
 *   8a. Edit the Claude-drafted reply (editable textarea)
 *   9a. Build + download the threaded .eml
 *   7b. (optional) add recipients to the gated link
 *   8b. Done - share URL + copy / open buttons
 *
 * Refactor notes:
 *   - The "Grids only", "Original PDF", and "Supplier RFQs" tiles
 *     moved to a dedicated /exports page so the wizard stays focused.
 *   - The pre-mint recipient chip input is GONE from the upfront
 *     flow. In the reply path, recipients are auto-derived from the
 *     uploaded .msg and persisted via POST /proposals/share/<token>/
 *     recipients after mint. In the just-link path, an inline chip
 *     editor lets staff tack recipients on or skip and configure
 *     later via /deals.
 *   - Each step renders an "Edit" affordance that snaps back so
 *     staff can correct a wrong answer without restarting.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Loader2,
  Mail,
  Link2,
  Sparkles,
  RotateCcw,
  Pencil,
  Plus,
  X,
  AlertCircle,
  Send,
  FileDown,
  Download,
  Upload,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import AttachmentReviewCard from "@/components/share/AttachmentReviewCard";
import AttachmentsSection, {
  flushQueueToToken,
} from "../components/share/AttachmentsSection";
import FingerprintLookup from "../components/staff/FingerprintLookup";
import { renderApiError } from "../lib/auth";
import { useDraftJob } from "../lib/draftJob";

const API = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = "medicys_recent_links";
const RECENT_MAX = 5;

const PERSONAL_DOMAINS = new Set([
  "gmail.com", "googlemail.com",
  "yahoo.com", "yahoo.co.uk", "yahoo.fr", "yahoo.de", "yahoo.es", "ymail.com",
  "hotmail.com", "hotmail.co.uk", "hotmail.fr",
  "outlook.com", "outlook.fr", "outlook.de",
  "live.com", "live.co.uk",
  "msn.com", "icloud.com", "me.com", "mac.com",
  "aol.com", "aim.com",
  "proton.me", "protonmail.com",
  "gmx.com", "gmx.de", "gmx.net",
  "mail.com",
  "yandex.com", "yandex.ru",
  "zoho.com", "fastmail.com",
]);
const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@([A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;

// ---------------------------------------------------------------------------
// Shared "friendly hyperlink" helpers
// ---------------------------------------------------------------------------
// Every "Copy link" button in the wizard should end up producing the
// same result: a rich clipboard payload that pastes as a proper
// blue-underlined hyperlink in Outlook/Word/Gmail, with anchor text
// ``<Project_Reference> (<entry_number>)`` matching the label the
// .eml uses. Extracted here so the Preview step, the .eml Done step,
// and the just-link Done step all share the identical behaviour.

function buildProposalLinkLabel(mintResult) {
  const ref = (mintResult?.project_reference || "").trim();
  const num = mintResult?.entry_number;
  if (ref && num) return `${ref} (${num})`;
  if (ref) return ref;
  if (num) return `Proposal ${num}`;
  return mintResult?.shareUrl || "";
}

async function copyProposalLinkAsHyperlink(mintResult) {
  // "Copy link" writes BOTH text/html AND text/plain to the
  // clipboard in one shot. Outlook/Word paste picks up the HTML and
  // renders a proper hyperlink whose anchor text is the friendly
  // label. Plain-text destinations (Slack, Notepad, terminal) get a
  // clean "LABEL: URL" fallback so the label + link are still both
  // visible without any formatting.
  const url = mintResult?.shareUrl || "";
  const label = buildProposalLinkLabel(mintResult);
  const escapedLabel = label
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const html = `<a href="${url}">${escapedLabel}</a>`;
  const plain = `${label}: ${url}`;
  try {
    // Modern rich-clipboard path - Chrome, Edge, Safari 13.4+, Firefox 127+.
    if (
      navigator.clipboard &&
      typeof window.ClipboardItem !== "undefined"
    ) {
      const item = new window.ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plain], { type: "text/plain" }),
      });
      await navigator.clipboard.write([item]);
    } else {
      // Older browsers - degrade to plain text with the label.
      await navigator.clipboard.writeText(plain);
    }
    toast.success(
      `Copied: ${label}. Paste into Outlook or Word for a clickable hyperlink; plain-text apps get "${label}: <url>".`,
      { duration: 6000 },
    );
  } catch {
    // Last-ditch fallback if the async Clipboard API is blocked
    // (some corporate policies do this) - drop the URL alone.
    try {
      await navigator.clipboard?.writeText(url);
      toast.info(
        "Copied the URL. Rich hyperlink copy isn't available in this browser.",
        { duration: 6000 },
      );
    } catch {
      toast.error(
        "Copy failed. Select the URL below and press Ctrl+C.",
        { duration: 6000 },
      );
    }
  }
}

const STEP_LABELS = {
  entry: "1 · Proposal",
  cluster: "1b · Related options",
  grid: "2 · Their cost grid",
  prepare: "3 · Prepare link",
  preview: "4 · Preview",
  confirm: "5 · Confirm preview",
  next: "6 · What's next?",
  msgUpload: "7 · Client email",
  draft: "8 · Edit reply",
  emlDone: "9 · Download",
  recipients: "7 · Recipients",
  linkDone: "8 · Done",
};

export default function ShareProposal() {
  // ---- state ------------------------------------------------------
  const [step, setStep] = useState("entry");
  // Tracks which steps have been actually entered via a Continue
  // click. Step N renders iff its id is in this set - prevents the
  // "Step 2 peeking through dimly before I press Continue" UX bug
  // staff reported. Edit-back uses goTo() too, so it never SHRINKS:
  // once a step has been visited, it stays available below to keep
  // its summary line in view.
  const [unlocked, setUnlocked] = useState(() => new Set(["entry"]));
  const [entryNumber, setEntryNumber] = useState("");
  const [hasClientGrid, setHasClientGrid] = useState(null); // true / false
  const [attachmentQueue, setAttachmentQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const [mintResult, setMintResult] = useState(null);
  // Iter-68: attachment review gate - must be re-ticked on every mint.
  const [attachmentsConfirmed, setAttachmentsConfirmed] = useState(false);
  const [recent, setRecent] = useState([]);
  const [parsedMsg, setParsedMsg] = useState(null);
  const [draftBody, setDraftBody] = useState("");
  // Draft job lives in the global DraftJobProvider so polling
  // survives navigation away from /share (staff get a toast when
  // Claude finishes). Busy state derives from the job status.
  const {
    job: draftJob,
    startDraftJob,
    clearJob: clearDraftJob,
  } = useDraftJob();
  const draftBusy = draftJob?.status === "pending";
  const [emlBusy, setEmlBusy] = useState(false);
  const [emlDone, setEmlDone] = useState(false);
  // Blob URL + filename for the freshly-built reply .eml. Stashed in
  // state (not just used for an auto-click) so Step 9 can render a
  // VISIBLE "Download .eml" link the staff member can click again
  // if the browser ate the synthetic click. Some browsers (older
  // Safari, strict-CSP environments) cancel a programmatic download
  // when the object-URL is revoked synchronously after `.click()` -
  // keeping the URL alive as state + a real anchor element is the
  // robust pattern. The URL is revoked when the wizard restarts.
  const [emlBlobUrl, setEmlBlobUrl] = useState(null);
  const [emlFilename, setEmlFilename] = useState("reply.eml");
  // Resolved recipients returned by /draft-reply - same list the
  // backend uses when assembling the .eml so the preview and the
  // download never disagree.
  const [resolvedTo, setResolvedTo] = useState([]);
  const [resolvedCc, setResolvedCc] = useState([]);
  const [alwaysCc, setAlwaysCc] = useState([]);
  // Recipients editor used in the "just-link" path only.
  const [linkRecipients, setLinkRecipients] = useState([]);
  const [linkRecipientDraft, setLinkRecipientDraft] = useState("");
  const [recipientsBusy, setRecipientsBusy] = useState(false);

  // Iter-43: OCID cluster discovery + bundle state.
  // `clusterOptions` holds the discovered family (root + descendants
  // filtered to Commissioned=No) for the entry the staff just
  // punched in. `selectedEntries` is a Set of entry_numbers ticked
  // for inclusion in the bundle - always starts pre-populated with
  // the whole cluster so staff untick rather than tick.
  // `clusterBusy` blocks the Continue button while the discovery
  // endpoint is in-flight.
  const [clusterOptions, setClusterOptions] = useState([]);
  const seedLifecycle = clusterOptions.find(
    (o) => Number(o.cid) === parseInt(entryNumber, 10)
  )?.lifecycle;
  const [selectedEntries, setSelectedEntries] = useState(() => new Set());
  const [clusterBusy, setClusterBusy] = useState(false);

  // Helper - advance to a step AND unlock it. All step transitions
  // route through this so the unlocked set stays in sync.
  const goTo = (next) => {
    setStep(next);
    setUnlocked((prev) => {
      if (prev.has(next)) return prev;
      const merged = new Set(prev);
      merged.add(next);
      return merged;
    });
  };

  // Load recent links from localStorage.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      if (raw) setRecent(JSON.parse(raw) || []);
    } catch {
      /* ignore */
    }
  }, []);

  const pushRecent = (entry) => {
    setRecent((prev) => {
      const filtered = prev.filter(
        (r) => r.entry_number !== entry.entry_number,
      );
      const next = [entry, ...filtered].slice(0, RECENT_MAX);
      try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // ---- draft-job resume + consume ---------------------------------
  // RESUME (mount only): staff navigated away while Claude was
  // drafting (or after it finished) and came back to /share. The
  // provider still holds the wizard context, so rebuild enough state
  // to land straight on the draft step instead of a blank wizard.
  useEffect(() => {
    if (!draftJob || mintResult) return;
    if (!draftJob.mintResult || !draftJob.parsedMsg) return;
    if (draftJob.status !== "pending" && draftJob.status !== "done") return;
    setMintResult(draftJob.mintResult);
    setParsedMsg(draftJob.parsedMsg);
    setEntryNumber(String(draftJob.mintResult.entry_number || ""));
    setHasClientGrid(false);
    setAttachmentsConfirmed(true);
    setUnlocked(
      new Set([
        "entry",
        "grid",
        "prepare",
        "preview",
        "confirm",
        "next",
        "msgUpload",
        "draft",
      ]),
    );
    setStep("draft");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CONSUME: hydrate the draft editor whenever the job resolves
  // (fires once per jobId+status transition; also on remount when a
  // finished job is being resumed). On error, bounce off the draft
  // spinner back to the .msg upload step so staff can retry - the
  // provider already showed the error toast.
  useEffect(() => {
    if (!draftJob) return;
    if (draftJob.status === "done" && draftJob.result) {
      setDraftBody(draftJob.result.draft_body || "");
      setResolvedTo(draftJob.result.resolved_to || []);
      setResolvedCc(draftJob.result.resolved_cc || []);
      setAlwaysCc(draftJob.result.always_cc || []);
    } else if (draftJob.status === "error") {
      setStep((s) => (s === "draft" ? "msgUpload" : s));
      setUnlocked((prev) => {
        if (!prev.has("draft")) return prev;
        const next = new Set(prev);
        next.delete("draft");
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftJob?.jobId, draftJob?.status]);

  // ---- step transitions -------------------------------------------
  const restart = () => {
    setStep("entry");
    setUnlocked(new Set(["entry"]));
    setEntryNumber("");
    setHasClientGrid(null);
    setAttachmentQueue([]);
    setMintResult(null);
    setAttachmentsConfirmed(false);
    setParsedMsg(null);
    setDraftBody("");
    clearDraftJob();
    setEmlDone(false);
    if (emlBlobUrl) URL.revokeObjectURL(emlBlobUrl);
    setEmlBlobUrl(null);
    setEmlFilename("reply.eml");
    setResolvedTo([]);
    setResolvedCc([]);
    setAlwaysCc([]);
    setLinkRecipients([]);
    setLinkRecipientDraft("");
    setClusterOptions([]);
    setSelectedEntries(new Set());
  };

  /**
   * Wipe the email-intake state (parsed .msg, AI draft, resolved
   * recipients) so the staff member can drop a different ``.msg``
   * file. Step 8 (draft) and 9 (emlDone) are removed from the
   * unlocked set since their content is now invalidated; the user
   * is bounced back to step 7 (msgUpload).
   */
  const resetEmailIntake = () => {
    setParsedMsg(null);
    setDraftBody("");
    clearDraftJob();
    setResolvedTo([]);
    setResolvedCc([]);
    setAlwaysCc([]);
    setEmlDone(false);
    if (emlBlobUrl) URL.revokeObjectURL(emlBlobUrl);
    setEmlBlobUrl(null);
    setEmlFilename("reply.eml");
    setUnlocked((prev) => {
      const next = new Set(prev);
      next.delete("draft");
      next.delete("emlDone");
      return next;
    });
    setStep("msgUpload");
  };

  // Iter-43: OCID cluster discovery. Called from the "entry" step's
  // Continue click. Populates ``clusterOptions`` and pre-ticks all
  // discovered options in ``selectedEntries``. If the cache doesn't
  // know the seed (``error: 'not_in_cache'``) we DO NOT block - the
  // wizard falls straight through to the cost-grid step with a
  // single-entry selection. If the cluster contains only the seed
  // (no siblings) we also skip the picker step and go straight to
  // cost-grid so single-link staff don't see an unnecessary panel.
  const discoverCluster = async (opts = {}) => {
    const { refresh = false } = opts;
    const n = parseInt(entryNumber, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a valid Cognito proposal number.");
      return;
    }
    setClusterBusy(true);
    try {
      const { data } = await axios.get(
        `${API}/api/proposals/clusters/${n}` +
          (refresh ? "?refresh=true" : ""),
        // Cognito OData refresh can take 30-90 s. Give it 120 s so
        // Cloudflare's ~100 s edge doesn't cut us off mid-refresh.
        refresh ? { timeout: 120000 } : undefined,
      );
      const returned = Array.isArray(data?.options) ? data.options : [];
      setClusterOptions(returned);
      const preSelected = new Set(returned.map((o) => o.cid));
      // Always tick the seed itself, even if the cache missed it.
      preSelected.add(n);
      setSelectedEntries(preSelected);
      const pending = Number(data?.pending_sharepoint || 0);
      if (returned.length <= 1) {
        if (refresh) {
          // User explicitly asked to re-sync and still no siblings.
          const r = data?.refresh || {};
          toast.info(
            r.sharepoint_synced === false
              ? `SharePoint could not be re-synced (${r.sharepoint_error || "unknown error"}). Only proposal #${n} is known in this OCID family for now.`
              : `Only proposal #${n} exists in this OCID family. SharePoint was re-synced and Cognito read live: check the clone's OCID field in Cognito points at #${n} (or its parent).`,
          );
        }
        // No siblings - fall straight through to the existing
        // single-link cost-grid step.
        goTo("grid");
      } else {
        if (refresh) {
          toast.success(
            `Refreshed: found ${returned.length} proposals in this family${pending ? ` (${pending} not yet in SharePoint, read from Cognito)` : ""}.`,
          );
        }
        // Show the cluster picker before cost-grid.
        goTo("cluster");
      }
    } catch (err) {
      // Discovery is a convenience, staff can still continue with the
      // single entry they typed, but they must know it did NOT run.
      // eslint-disable-next-line no-console
      console.warn("cluster discovery failed", err);
      const timedOut = err?.code === "ECONNABORTED" || [502, 503, 504].includes(err?.response?.status);
      toast.error(
        timedOut
          ? "Checking related proposals timed out. Continue with this proposal, or try Refresh again in a minute."
          : `Could not check related proposals: ${err?.response?.data?.detail || err?.message || "unknown error"}.`,
      );
      setClusterOptions([]);
      setSelectedEntries(new Set([n]));
      goTo("grid");
    } finally {
      setClusterBusy(false);
    }
  };

  const toggleSelectedEntry = (cid) => {
    setSelectedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) {
        next.delete(cid);
      } else {
        next.add(cid);
      }
      return next;
    });
  };

  const onPrepareLink = async () => {
    const n = parseInt(entryNumber, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error("Enter a proposal number first.");
      return;
    }
    // Iter-43: if the staff ticked more than one option in the
    // cluster picker, use the bundle-mint endpoint. Otherwise fall
    // through to the single-link path exactly as before.
    const entryList =
      selectedEntries.size > 1
        ? Array.from(selectedEntries)
        : [n];
    if (entryList.length > 1) {
      await mintBundle(entryList);
      return;
    }
    setBusy(true);
    try {
      // Mint with no recipients - link enters `pending_config`. Staff
      // are bypassed by their auth session for preview; recipients
      // are attached later either from the parsed .msg (reply path)
      // or from the chip editor (just-link path).
      const { data } = await axios.post(`${API}/api/proposals/share`, {
        entry_number: n,
        recipient_emails: [],
      });
      const shareUrl = `${window.location.origin}${data.share_path}`;

      // Upload any queued attachments.
      let uploadedAttachments = [];
      if (attachmentQueue.length) {
        const results = await flushQueueToToken(data.token, attachmentQueue);
        uploadedAttachments = results.filter((r) => r.ok).map((r) => r.data);
        const failed = results.filter((r) => !r.ok);
        failed.forEach((f) =>
          toast.error(`Couldn't upload ${f.file.name}: ${f.error.replaceAll("_", " ")}`),
        );
      }

      // Fetch cumulative attachment list - re-mints of the same
      // proposal-token can have historical files we want surfaced.
      let totalAttachments = uploadedAttachments;
      try {
        const { data: listData } = await axios.get(
          `${API}/api/proposals/share/${data.token}/attachments`,
        );
        if (Array.isArray(listData?.attachments)) {
          totalAttachments = listData.attachments;
        }
      } catch {
        /* non-fatal */
      }

      const payload = {
        ...data,
        shareUrl,
        attachments: totalAttachments,
        uploadedThisMint: uploadedAttachments.length,
        uploadedIds: uploadedAttachments.map((a) => a.id),
      };
      setMintResult(payload);
      setAttachmentsConfirmed(false);
      pushRecent({
        entry_number: data.entry_number,
        token: data.token,
        project_reference: data.project_reference || "",
        client_company: data.client_company || "",
        shareUrl,
        minted_at: Date.now(),
        attachment_count: uploadedAttachments.length,
      });
      toast.success(`Link prepared for proposal #${n}`);
      goTo("preview");
    } catch (err) {
      toast.error(renderApiError(err, "Mint failed."));
    } finally {
      setBusy(false);
    }
  };

  const mintBundle = async (entryNumbers) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/proposals/share/bundle`,
        {
          entry_numbers: entryNumbers,
          recipient_emails: [],
        },
      );
      // Adapt the bundle response to the same shape the single-link
      // path uses so downstream steps (preview / recipients / done)
      // can operate on `mintResult.links` uniformly.
      const links = (data.links || []).map((L) => ({
        ...L,
        shareUrl: `${window.location.origin}${L.share_path}`,
        attachments: [],
        uploadedIds: [],
      }));
      // The queued cost grid goes on EVERY option's link, so the banner
      // shows whichever option the client opens.
      for (const L of links) {
        let uploaded = [];
        if (attachmentQueue.length) {
          const results = await flushQueueToToken(L.token, attachmentQueue);
          uploaded = results.filter((r) => r.ok).map((r) => r.data);
          results
            .filter((r) => !r.ok)
            .forEach((f) =>
              toast.error(
                `Couldn't attach ${f.file.name} to #${L.entry_number}: ${f.error.replaceAll("_", " ")}`,
              ),
            );
        }
        L.attachments = uploaded;
        L.uploadedIds = uploaded.map((a) => a.id);
        try {
          const { data: listData } = await axios.get(
            `${API}/api/proposals/share/${L.token}/attachments`,
          );
          if (Array.isArray(listData?.attachments)) L.attachments = listData.attachments;
        } catch {
          /* non-fatal */
        }
      }
      const primary = links[0] || {};
      const payload = {
        // Fields shared with the single-link mintResult shape.
        token: primary.token,
        entry_number: primary.entry_number,
        project_reference: primary.project_reference,
        client_company: primary.client_company,
        shareUrl: primary.shareUrl,
        share_path: primary.share_path,
        attachments: primary.attachments || [],
        uploadedIds: primary.uploadedIds || [],
        // Bundle-specific.
        bundle_id: data.bundle_id,
        entry_numbers: data.entry_numbers,
        links,
      };
      setMintResult(payload);
      setAttachmentsConfirmed(false);
      links.forEach((L) => {
        pushRecent({
          entry_number: L.entry_number,
          token: L.token,
          project_reference: L.project_reference || "",
          client_company: L.client_company || "",
          shareUrl: L.shareUrl,
          minted_at: Date.now(),
          attachment_count: (L.uploadedIds || []).length,
        });
      });
      const attachedCount = links.reduce((n, L) => n + (L.uploadedIds || []).length, 0);
      toast.success(
        attachedCount
          ? `Bundle prepared: ${links.length} options minted, ${attachmentQueue.length} file${attachmentQueue.length === 1 ? "" : "s"} attached to each.`
          : `Bundle prepared: ${links.length} options minted.`,
      );
      goTo("preview");
    } catch (err) {
      toast.error(renderApiError(err, "Bundle mint failed."));
    } finally {
      setBusy(false);
    }
  };

  const onParseMsg = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".msg")) {
      toast.error("Please upload an Outlook .msg file.");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(
        `${API}/api/proposals/intake/parse-msg`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setParsedMsg(data);

      // If the .msg yielded recipients, attach them to the existing
      // gated link so the client can actually open it.
      if (data.suggested_recipients?.length && mintResult?.token) {
        try {
          await axios.post(
            `${API}/api/proposals/share/${mintResult.token}/recipients`,
            { recipient_emails: data.suggested_recipients },
          );
          toast.success(
            `Recipients attached: ${data.suggested_recipients.join(", ")}`,
          );
        } catch (recipErr) {
          toast.error(
            renderApiError(recipErr, "Couldn't attach recipients to the link."),
          );
        }
      }

      // Auto-fire the draft. It runs as a BACKGROUND JOB (two Claude
      // passes can take 60-120s on long threads) watched by the
      // global DraftJobProvider - staff can navigate away and get a
      // toast when it's ready; the effects below consume the result.
      try {
        await startDraftJob({
          payload: {
            share_url: mintResult.shareUrl,
            project_reference: mintResult.project_reference || "",
            client_company: mintResult.client_company || "",
            entry_number: mintResult.entry_number || null,
            // Iter-44: pass bundle_id when the wizard minted a
            // multi-option bundle so the drafter renders one
            // hyperlink per option + common/delta assumption split.
            bundle_id: mintResult.bundle_id || null,
            from_email: data.from_email,
            from_name: data.from_name || "",
            to: data.to || [],
            cc: data.cc || [],
            subject: data.subject || "",
            body: data.body || "",
            body_html: data.body_html || "",
            inline_images: data.inline_images || [],
            message_id: data.message_id || "",
            references: data.references || "",
            sent_at: data.sent_at || "",
          },
          mintResult,
          parsedMsg: data,
        });
        goTo("draft");
      } catch (drErr) {
        toast.error(renderApiError(drErr, "Couldn't draft the reply."));
      }
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't parse the email."));
    } finally {
      setBusy(false);
    }
  };

  const onBuildEml = async () => {
    if (!parsedMsg || !mintResult) return;
    if (!draftBody.trim()) {
      toast.error("The reply body is empty - add a few words first.");
      return;
    }
    setEmlBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/proposals/intake/build-reply`,
        {
          share_url: mintResult.shareUrl,
          project_reference: mintResult.project_reference || "",
          client_company: mintResult.client_company || "",
          entry_number: mintResult.entry_number || null,
          // Iter-44: preserve bundle context on the final .eml build
          // so downloaded reply matches the previewed draft when the
          // wizard minted multiple sibling options.
          bundle_id: mintResult.bundle_id || null,
          from_email: parsedMsg.from_email,
          from_name: parsedMsg.from_name || "",
          to: parsedMsg.to || [],
          cc: parsedMsg.cc || [],
          subject: parsedMsg.subject || "",
          body: parsedMsg.body || "",
          body_html: parsedMsg.body_html || "",
          inline_images: parsedMsg.inline_images || [],
          message_id: parsedMsg.message_id || "",
          references: parsedMsg.references || "",
          sent_at: parsedMsg.sent_at || "",
          edited_body: draftBody,
        },
        { responseType: "blob" },
      );
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : "reply.eml";
      // `res.data` is already a Blob when responseType="blob". Skip
      // the unnecessary re-wrap (some browsers misbehave with a
      // Blob-inside-Blob construction).
      const blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], { type: "message/rfc822" });
      const url = URL.createObjectURL(blob);

      // Stash for the visible anchor on Step 9. Revoke any previous
      // url so we don't leak memory if staff re-builds the same draft.
      setEmlBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
      setEmlFilename(filename);

      // Best-effort auto-download. We DON'T revoke the URL after the
      // click because some browsers (older Safari, strict-CSP setups)
      // cancel the download when the source URL is freed synchronously
      // - that was the "Step 9 says downloaded but nothing in
      // downloads" bug staff reported. The URL is now revoked only
      // on restart or when a fresh build replaces it.
      try {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        // Keep the element in the DOM briefly so the click event
        // has time to commit to the download manager.
        document.body.appendChild(a);
        a.click();
        setTimeout(() => a.remove(), 200);
      } catch {
        // If even the synthetic click fails (very locked-down
        // browsers), the visible anchor on Step 9 still works.
      }

      setEmlDone(true);
      // Flow complete - drop the global job so a later /share visit
      // starts fresh instead of resuming this finished draft.
      clearDraftJob();
      goTo("emlDone");
      toast.success("Reply built. If the download didn't start, click the link on the next step.");
    } catch (err) {
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          try {
            err.response.data = JSON.parse(text);
          } catch {
            err.response.data = text;
          }
        } catch {
          /* fallback */
        }
      }
      toast.error(renderApiError(err, "Couldn't build the reply .eml."));
    } finally {
      setEmlBusy(false);
    }
  };

  const onSaveLinkRecipients = async () => {
    if (!mintResult?.token) return;
    if (linkRecipients.length === 0) {
      toast.error("Add at least one recipient - or use Skip below.");
      return;
    }
    setRecipientsBusy(true);
    try {
      await axios.post(
        `${API}/api/proposals/share/${mintResult.token}/recipients`,
        { recipient_emails: linkRecipients },
      );
      toast.success(
        `Link is now gated to ${linkRecipients.length} recipient${
          linkRecipients.length === 1 ? "" : "s"
        }.`,
      );
      goTo("linkDone");
    } catch (err) {
      toast.error(renderApiError(err, "Couldn't save recipients."));
    } finally {
      setRecipientsBusy(false);
    }
  };

  // ---- recipient helpers (just-link path) -------------------------
  const addLinkRecipient = (raw) => {
    const email = (raw || "").trim().toLowerCase();
    if (!email) return false;
    if (!EMAIL_RE.test(email)) {
      toast.error(`"${raw}" isn't a valid email.`);
      return false;
    }
    const domain = email.split("@")[1];
    if (PERSONAL_DOMAINS.has(domain)) {
      toast.error(`Personal email domain blocked (${domain}).`);
      return false;
    }
    if (linkRecipients.includes(email)) return true;
    setLinkRecipients((prev) => [...prev, email]);
    return true;
  };

  // ---- render -----------------------------------------------------
  return (
    <StaffShell>
      <div data-testid="share-wizard" className="space-y-6">
        <header>
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-2">
            Mint a client-ready proposal
          </p>
          <h1 className="text-3xl sm:text-4xl font-serif font-light text-slate-900 leading-tight">
            Step-by-step share.
          </h1>
          <p className="text-sm text-slate-500 leading-relaxed max-w-xl mt-2">
            Answer a few questions and we&apos;ll prepare the link, the
            preview, and a perfectly threaded reply email - all in one
            place.
          </p>
        </header>

        <ol className="space-y-3">
          {/* STEP 1 - proposal number */}
          <StepCard
            id="entry"
            active={step === "entry"}
            done={step !== "entry"}
            label={STEP_LABELS.entry}
            title={
              step === "entry"
                ? "Which proposal are we sharing?"
                : `Proposal #${entryNumber}`
            }
            onEdit={() => setStep("entry")}
            collapsedSummary={
              step !== "entry" && entryNumber ? `Cognito #${entryNumber}` : null
            }
            testid="step-entry"
          >
            <StepEntry
              value={entryNumber}
              onChange={setEntryNumber}
              onContinue={discoverCluster}
              busy={clusterBusy}
            />
          </StepCard>

          {/* Closed execution warning: SharePoint deleted this row
              (option not selected / opportunity lost). */}
          {step !== "entry" && seedLifecycle?.closed && (
            <div
              data-testid="wizard-seed-closed-warning"
              className="rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800"
            >
              <p className="font-semibold">
                Proposal #{entryNumber} is {seedLifecycle.status} in SharePoint.
              </p>
              <p className="mt-0.5 text-rose-700">
                {seedLifecycle.note ? `${seedLifecycle.note}. ` : ""}
                Sharing a closed execution is unusual, double-check before you mint a link.
              </p>
            </div>
          )}

          {/* STEP 1b - Cluster picker (only when OCID siblings exist) */}
          {unlocked.has("cluster") && (
            <StepCard
              id="cluster"
              active={step === "cluster"}
              done={unlocked.has("cluster") && step !== "cluster"}
              label={STEP_LABELS.cluster}
              title="We found related options for this proposal"
              onEdit={() => setStep("cluster")}
              collapsedSummary={
                selectedEntries.size > 1
                  ? `Bundle of ${selectedEntries.size} options`
                  : "Single option"
              }
              testid="step-cluster"
            >
              <StepCluster
                clusterOptions={clusterOptions}
                selectedEntries={selectedEntries}
                onToggle={toggleSelectedEntry}
                onContinue={() => goTo("grid")}
              />
            </StepCard>
          )}

          {/* STEP 2 - Did the client provide their cost grid? */}
          {unlocked.has("grid") && (
            <StepCard
              id="grid"
              active={step === "grid"}
              done={unlocked.has("grid") && step !== "grid"}
              label={STEP_LABELS.grid}
              title="Did the client give us their cost grid?"
              onEdit={() => setStep("grid")}
              collapsedSummary={
                hasClientGrid === true
                  ? `Yes · ${attachmentQueue.length} file${
                      attachmentQueue.length === 1 ? "" : "s"
                    } queued`
                  : hasClientGrid === false
                    ? "No - proceeding without"
                    : null
              }
              testid="step-grid"
            >
              <StepCostGrid
                hasClientGrid={hasClientGrid}
                setHasClientGrid={setHasClientGrid}
                attachmentQueue={attachmentQueue}
                setAttachmentQueue={setAttachmentQueue}
                onContinue={() => goTo("prepare")}
                onRefreshCluster={() =>
                  discoverCluster({ refresh: true })
                }
                clusterRefreshBusy={clusterBusy}
                showRefreshClusterHint={
                  step === "grid" && selectedEntries.size <= 1
                }
              />
            </StepCard>
          )}

          {/* STEP 3 - Prepare link */}
          {unlocked.has("prepare") && (
            <StepCard
              id="prepare"
              active={step === "prepare"}
              done={unlocked.has("prepare") && step !== "prepare"}
              label={
                selectedEntries.size > 1
                  ? "3 · Prepare link(s)"
                  : STEP_LABELS.prepare
              }
              title={
                mintResult
                  ? selectedEntries.size > 1
                    ? "Link(s) prepared"
                    : "Link prepared"
                  : selectedEntries.size > 1
                    ? "Prepare the share link(s)"
                    : "Prepare the share link"
              }
              onEdit={mintResult ? null : () => setStep("prepare")}
              collapsedSummary={
                mintResult ? mintResult.shareUrl : null
              }
              testid="step-prepare"
            >
              <StepPrepare
                busy={busy}
                onPrepareLink={onPrepareLink}
                hasClientGrid={hasClientGrid}
                attachmentCount={attachmentQueue.length}
                entryNumber={entryNumber}
                selectedCount={selectedEntries.size}
              />
            </StepCard>
          )}

          {/* STEP 4 - Preview */}
          {unlocked.has("preview") && (
            <StepCard
              id="preview"
              active={step === "preview"}
              done={unlocked.has("preview") && step !== "preview"}
              label={STEP_LABELS.preview}
              title="Preview the client experience"
              onEdit={() => setStep("preview")}
              collapsedSummary="Preview opened"
              testid="step-preview"
            >
              <StepPreview
                mintResult={mintResult}
                onContinue={() => goTo("confirm")}
                attachmentsConfirmed={attachmentsConfirmed}
                onAttachmentsConfirmChange={setAttachmentsConfirmed}
                onAttachmentsChanged={(list, token) =>
                  setMintResult((prev) => {
                    if (!prev) return prev;
                    if (Array.isArray(prev.links) && prev.links.length > 1) {
                      return {
                        ...prev,
                        links: prev.links.map((L) =>
                          L.token === token ? { ...L, attachments: list } : L,
                        ),
                      };
                    }
                    return { ...prev, attachments: list };
                  })
                }
              />
            </StepCard>
          )}

          {/* STEP 5 - Confirm preview */}
          {unlocked.has("confirm") && (
              <StepCard
                id="confirm"
                active={step === "confirm"}
                done={unlocked.has("confirm") && step !== "confirm"}
                label={STEP_LABELS.confirm}
                title="Happy with what your client will see?"
                onEdit={() => setStep("confirm")}
                collapsedSummary="Confirmed"
                testid="step-confirm"
              >
                <StepConfirm
                  onYes={() => goTo("next")}
                  onBack={() => setStep("preview")}
                />
              </StepCard>
            )}

          {/* STEP 6 - choose path */}
          {unlocked.has("next") && (
            <StepCard
              id="next"
              active={step === "next"}
              done={unlocked.has("next") && step !== "next"}
              label={STEP_LABELS.next}
              title="What's next?"
              onEdit={() => setStep("next")}
              collapsedSummary={
                ["msgUpload", "draft", "emlDone"].includes(step)
                  ? "Reply to a client email"
                  : ["recipients", "linkDone"].includes(step)
                    ? "Just give me the link"
                    : null
              }
              testid="step-next"
            >
              <StepNextChoice
                onReply={() => goTo("msgUpload")}
                onJustLink={() => goTo("recipients")}
              />
            </StepCard>
          )}

          {/* STEP 7a - upload .msg */}
          {unlocked.has("msgUpload") && (
            <StepCard
              id="msgUpload"
              active={step === "msgUpload"}
              done={unlocked.has("msgUpload") && step !== "msgUpload"}
              label={STEP_LABELS.msgUpload}
              title="Upload the client's email"
              onEdit={resetEmailIntake}
              collapsedSummary={
                parsedMsg
                  ? `From: ${parsedMsg.from_email}${
                      parsedMsg.subject ? ` · "${parsedMsg.subject}"` : ""
                    }`
                  : null
              }
              testid="step-msg-upload"
            >
              <StepMsgUpload
                onParse={onParseMsg}
                parsing={busy || draftBusy}
                parsed={parsedMsg}
                onReset={resetEmailIntake}
              />
            </StepCard>
          )}

          {/* STEP 8a - edit reply */}
          {unlocked.has("draft") && (
            <StepCard
              id="draft"
              active={step === "draft"}
              done={unlocked.has("draft") && step !== "draft"}
              label={STEP_LABELS.draft}
              title="Review &amp; edit the AI-drafted reply"
              onEdit={() => setStep("draft")}
              collapsedSummary={
                step === "emlDone"
                  ? `${draftBody.length} chars · downloaded`
                  : null
              }
              testid="step-draft"
            >
              <StepDraft
                draftBody={draftBody}
                setDraftBody={setDraftBody}
                draftBusy={draftBusy}
                emlBusy={emlBusy}
                resolvedTo={resolvedTo}
                resolvedCc={resolvedCc}
                alwaysCc={alwaysCc}
                onResetEmail={resetEmailIntake}
                onConfirm={onBuildEml}
              />
            </StepCard>
          )}

          {/* STEP 9a - eml downloaded */}
          {unlocked.has("emlDone") && (
            <StepCard
              id="emlDone"
              active
              done={false}
              label={STEP_LABELS.emlDone}
              title="Reply downloaded"
              onEdit={null}
              collapsedSummary={null}
              testid="step-eml-done"
            >
              <StepEmlDone
                emlFilename={emlFilename}
                onRestart={restart}
              />
            </StepCard>
          )}

          {/* STEP 7b - recipients (just-link path) */}
          {unlocked.has("recipients") && (
            <StepCard
              id="recipients"
              active={step === "recipients"}
              done={unlocked.has("recipients") && step !== "recipients"}
              label={STEP_LABELS.recipients}
              title="Who can open this link?"
              onEdit={() => setStep("recipients")}
              collapsedSummary={
                linkRecipients.length
                  ? `${linkRecipients.length} recipient${
                      linkRecipients.length === 1 ? "" : "s"
                    } · ${linkRecipients.map((e) => e.split("@")[1]).join(", ")}`
                  : "Skipped - link still locked"
              }
              testid="step-recipients"
            >
              <StepRecipients
                recipients={linkRecipients}
                setRecipients={setLinkRecipients}
                draft={linkRecipientDraft}
                setDraft={setLinkRecipientDraft}
                add={addLinkRecipient}
                busy={recipientsBusy}
                onSave={onSaveLinkRecipients}
                onSkip={() => goTo("linkDone")}
              />
            </StepCard>
          )}

          {/* STEP 8b - link done (just-link path) */}
          {unlocked.has("linkDone") && (
            <StepCard
              id="linkDone"
              active
              done={false}
              label={STEP_LABELS.linkDone}
              title="All set"
              onEdit={null}
              collapsedSummary={null}
              testid="step-link-done"
            >
              <StepLinkDone
                mintResult={mintResult}
                recipientCount={linkRecipients.length}
                recipientEmails={linkRecipients}
                onRestart={restart}
              />
            </StepCard>
          )}
        </ol>

        {/* Recent + forensic - collapsed below the wizard. */}
        {recent.length > 0 && (
          <section data-testid="recent-links-section" className="pt-4">
            <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-stone-500 mb-3">
              Recent links
            </p>
            <ul className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-200 overflow-hidden shadow-sm">
              {recent.map((item) => (
                <li
                  key={item.entry_number}
                  className="px-4 py-3 hover:bg-stone-50 transition-colors group"
                  data-testid={`recent-link-${item.entry_number}`}
                >
                  <a
                    href={item.shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3"
                  >
                    <span className="font-mono text-xs text-stone-400 tabular-nums w-14 shrink-0">
                      #{item.entry_number}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-800 truncate">
                        {item.project_reference || (
                          <span className="italic text-stone-400">
                            untitled
                          </span>
                        )}
                      </p>
                      <p className="text-[10px] text-stone-400 font-mono mt-0.5 truncate">
                        {item.client_company || "-"} · {item.shareUrl}
                      </p>
                    </div>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="pt-4">
          <FingerprintLookup />
        </div>
      </div>
    </StaffShell>
  );
}

/* ====================================================================
   Building blocks - small step components kept in-file so the whole
   wizard state machine remains visible at a glance.
   ==================================================================== */

function StepCard({
  active,
  done,
  label,
  title,
  collapsedSummary,
  onEdit,
  children,
  testid,
}) {
  return (
    <li
      data-testid={testid}
      className={`bg-white border rounded-2xl p-5 sm:p-6 transition-all shadow-sm ${
        active
          ? "border-[#1B4F8A] shadow-[0_8px_32px_-12px_rgba(27,79,138,0.18)]"
          : "border-stone-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <p
            className={`text-[10px] font-mono tracking-[0.22em] uppercase mb-1 ${
              active
                ? "text-[#1B4F8A]"
                : done
                  ? "text-emerald-700"
                  : "text-stone-400"
            }`}
          >
            {done && <CheckCircle2 className="w-3 h-3 inline mr-1.5 -mt-0.5" />}
            {label}
          </p>
          <h2 className="text-base sm:text-lg font-medium text-slate-900 leading-snug">
            {title}
          </h2>
          {!active && done && collapsedSummary && (
            <p className="text-xs text-stone-500 mt-1 truncate font-mono">
              {collapsedSummary}
            </p>
          )}
        </div>
        {!active && done && onEdit && (
          <button
            type="button"
            onClick={onEdit}
            data-testid={`${testid}-edit-btn`}
            className="text-[10px] font-mono tracking-[0.14em] uppercase text-stone-500 hover:text-[#1B4F8A] inline-flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-stone-50 transition-colors shrink-0"
          >
            <Pencil className="w-3 h-3" />
            Edit
          </button>
        )}
      </div>
      {active && <div>{children}</div>}
    </li>
  );
}

function StepEntry({ value, onChange, onContinue, busy }) {
  return (
    <div>
      <div className="relative bg-stone-50 border border-stone-200 rounded-xl overflow-hidden focus-within:border-[#3EA8DC] focus-within:bg-white transition-colors">
        <input
          type="number"
          min="1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onContinue();
            }
          }}
          placeholder="e.g. 6912"
          autoFocus
          data-testid="wizard-entry-number-input"
          className="w-full bg-transparent px-5 py-4 text-3xl sm:text-4xl font-mono text-slate-900 placeholder:text-stone-300 focus:outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          inputMode="numeric"
        />
      </div>
      <p className="text-[11px] text-stone-500 mt-2 font-mono">
        Tip - use the number from the Cognito form URL. If related
        options exist (via OCID), we&apos;ll offer them next.
      </p>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          disabled={!value.trim() || busy}
          data-testid="wizard-entry-continue"
          className="inline-flex items-center gap-1.5 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          {busy ? "Checking..." : "Continue"}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}


function StepCluster({ clusterOptions, selectedEntries, onToggle, onContinue }) {
  const totalTicked = selectedEntries.size;
  return (
    <div>
      <p className="text-sm text-stone-700 leading-relaxed mb-4">
        We spotted <b>{clusterOptions.length}</b> uncommissioned proposals
        in this OCID family (root plus copies). Untick anything you
        don&apos;t want to send. Send just one and we&apos;ll mint a
        single link. Send several and we&apos;ll build a single email
        with a proper hyperlink per option.
      </p>
      <div
        className="space-y-2 mb-6"
        data-testid="wizard-cluster-list"
      >
        {clusterOptions.map((opt) => {
          const ticked = selectedEntries.has(opt.cid);
          return (
            <label
              key={opt.cid}
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                ticked
                  ? "bg-emerald-50 border-emerald-300"
                  : "bg-white border-stone-200 hover:border-stone-400"
              }`}
              data-testid={`wizard-cluster-option-${opt.cid}`}
            >
              <input
                type="checkbox"
                checked={ticked}
                onChange={() => onToggle(opt.cid)}
                data-testid={`wizard-cluster-checkbox-${opt.cid}`}
                className="mt-1 w-4 h-4 accent-[#0d9488]"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono text-sm text-[#1B4F8A] font-semibold">
                    #{opt.cid}
                  </span>
                  {opt.ocid && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                      OCID {opt.ocid}
                    </span>
                  )}
                  {opt.status && (
                    <span
                      title={opt.lifecycle?.note || undefined}
                      data-testid={`wizard-cluster-status-${opt.cid}`}
                      className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        opt.lifecycle?.closed
                          ? opt.lifecycle.stage === "won"
                            ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
                            : "text-rose-700 bg-rose-50 border border-rose-200"
                          : "text-stone-600 bg-stone-100"
                      }`}
                    >
                      {opt.status}
                    </span>
                  )}
                  {opt.lifecycle?.closed && opt.lifecycle?.note && (
                    <span
                      className="text-[10px] text-stone-500 italic"
                      data-testid={`wizard-cluster-lifecycle-note-${opt.cid}`}
                    >
                      {opt.lifecycle.note}
                    </span>
                  )}
                  {opt.pending_sharepoint && (
                    <span
                      className="text-[10px] font-mono uppercase tracking-wider text-[#1B4F8A] bg-[#3EA8DC]/10 border border-[#3EA8DC]/40 px-1.5 py-0.5 rounded"
                      data-testid={`wizard-cluster-pending-${opt.cid}`}
                      title="Read live from Cognito; SharePoint has not synced this entry yet"
                    >
                      New in Cognito
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-900 mt-0.5 leading-snug break-words">
                  {opt.project_name || "(untitled)"}
                </p>
                <p className="text-[11px] text-stone-500 mt-0.5 font-mono">
                  {[opt.client_company, opt.poc]
                    .filter(Boolean)
                    .join(" · ")}
                  {opt.turnover
                    ? ` · £${Number(opt.turnover).toLocaleString()}`
                    : ""}
                </p>
              </div>
            </label>
          );
        })}
      </div>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-stone-600 font-mono">
          {totalTicked} option{totalTicked === 1 ? "" : "s"} selected
          {totalTicked > 1 ? " · will mint a bundle" : ""}
        </p>
        <button
          type="button"
          onClick={onContinue}
          disabled={totalTicked === 0}
          data-testid="wizard-cluster-continue"
          className="inline-flex items-center gap-1.5 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          Continue
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function StepCostGrid({
  hasClientGrid,
  setHasClientGrid,
  attachmentQueue,
  setAttachmentQueue,
  onContinue,
  onRefreshCluster,
  clusterRefreshBusy,
  showRefreshClusterHint,
}) {
  const canContinue =
    hasClientGrid === false ||
    (hasClientGrid === true && attachmentQueue.length > 0);
  return (
    <div>
      {showRefreshClusterHint && (
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900"
          data-testid="wizard-refresh-cluster-hint"
        >
          <p className="mb-2">
            Only <b>this proposal</b> was found in the OCID family. If you have
            just cloned this entry or retrofitted an OCID in Cognito (linking
            related proposals), click <b>Refresh</b>: it re-syncs SharePoint and
            reads Cognito live, which takes about 30 seconds.
          </p>
          <button
            type="button"
            onClick={onRefreshCluster}
            disabled={clusterRefreshBusy}
            data-testid="wizard-refresh-cluster-btn"
            className="inline-flex items-center gap-2 text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-800 disabled:opacity-50"
          >
            {clusterRefreshBusy ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Re-syncing SharePoint and reading Cognito…
              </>
            ) : (
              <>Refresh siblings from Cognito</>
            )}
          </button>
        </div>
      )}
      <p className="text-sm text-stone-600 leading-relaxed mb-4">
        If the client sent a cost-grid template with their RFP, we&apos;ll
        attach the completed version to the share link so they can pull
        their populated grid back from the public proposal page in one
        click.
      </p>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <button
          type="button"
          onClick={() => setHasClientGrid(true)}
          data-testid="wizard-grid-yes"
          className={`text-left p-4 rounded-xl border-2 transition-all ${
            hasClientGrid === true
              ? "border-[#1B4F8A] bg-[#1B4F8A]/[0.04]"
              : "border-stone-200 hover:border-stone-300"
          }`}
        >
          <p className="text-xs font-mono tracking-[0.14em] uppercase text-[#1B4F8A] mb-1">
            Yes
          </p>
          <p className="text-sm text-slate-700">
            They sent a template - I&apos;ll upload the completed file.
          </p>
        </button>
        <button
          type="button"
          onClick={() => setHasClientGrid(false)}
          data-testid="wizard-grid-no"
          className={`text-left p-4 rounded-xl border-2 transition-all ${
            hasClientGrid === false
              ? "border-[#1B4F8A] bg-[#1B4F8A]/[0.04]"
              : "border-stone-200 hover:border-stone-300"
          }`}
        >
          <p className="text-xs font-mono tracking-[0.14em] uppercase text-[#1B4F8A] mb-1">
            No
          </p>
          <p className="text-sm text-slate-700">
            No template - share the proposal on its own.
          </p>
        </button>
      </div>

      {hasClientGrid === true && (
        <div
          data-testid="wizard-grid-uploader"
          className="mb-4 bg-stone-50/60 rounded-xl p-4 border border-stone-200"
        >
          <AttachmentsSection
            queue={attachmentQueue}
            onQueueChange={setAttachmentQueue}
            testIdPrefix="wizard-attachments"
          />
          {attachmentQueue.length === 0 && (
            <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3 shrink-0" />
              Add at least one file to continue.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="wizard-grid-continue"
          className="inline-flex items-center gap-1.5 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          Continue
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function StepPrepare({
  busy,
  onPrepareLink,
  hasClientGrid,
  attachmentCount,
  entryNumber,
  selectedCount = 1,
}) {
  const isMulti = selectedCount > 1;
  return (
    <div>
      <div className="bg-stone-50 rounded-xl p-4 mb-4 text-sm text-stone-700 space-y-1">
        <p>
          <span className="font-mono text-[#1B4F8A]">
            {isMulti ? "Proposals:" : "Proposal:"}
          </span>{" "}
          {isMulti ? `${selectedCount} options linked to #${entryNumber}` : `#${entryNumber}`}
        </p>
        <p>
          <span className="font-mono text-[#1B4F8A]">Client grid:</span>{" "}
          {hasClientGrid
            ? `Yes - ${attachmentCount} file${
                attachmentCount === 1 ? "" : "s"
              } to attach`
            : "No - proposal only"}
        </p>
      </div>
      <button
        type="button"
        onClick={onPrepareLink}
        disabled={busy}
        data-testid="wizard-prepare-link-btn"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-50 disabled:cursor-progress text-white text-sm font-medium px-6 py-3 rounded-lg transition-colors"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Link2 className="w-4 h-4" />
        )}
        {busy
          ? "Preparing…"
          : isMulti
            ? "Prepare link(s)"
            : "Prepare link"}
      </button>
    </div>
  );
}

function StepPreview({
  mintResult,
  onContinue,
  attachmentsConfirmed,
  onAttachmentsConfirmChange,
  onAttachmentsChanged,
}) {
  // Iter-43: when the wizard minted a bundle, mintResult carries a
  // ``links[]`` array with N entries. Render a tab strip so staff
  // can flip through each option's preview before committing.
  const isBundle = Array.isArray(mintResult?.links) && mintResult.links.length > 1;
  const [activeIdx, setActiveIdx] = useState(0);
  const activeLink = isBundle
    ? mintResult.links[Math.min(activeIdx, mintResult.links.length - 1)]
    : mintResult;
  const previewUrl = activeLink.shareUrl.includes("?")
    ? `${activeLink.shareUrl}&as=staff`
    : `${activeLink.shareUrl}?as=staff`;
  // Attachment review gate: whenever a link carries files (fresh
  // uploads OR leftovers from a previous share of the same entry),
  // the continue button stays locked until the checkbox is ticked.
  // For a bundle the card shows the active option's files.
  const reviewList = isBundle
    ? activeLink?.attachments || []
    : mintResult?.attachments || [];
  const anyAttached = isBundle
    ? mintResult.links.some((L) => (L.attachments || []).length > 0)
    : reviewList.length > 0;
  const gateLocked = anyAttached && !attachmentsConfirmed;
  return (
    <div>
      {reviewList.length > 0 && (
        <AttachmentReviewCard
          token={activeLink.token}
          attachments={reviewList}
          uploadedIds={isBundle ? activeLink.uploadedIds : mintResult.uploadedIds}
          onChanged={(list) => onAttachmentsChanged(list, activeLink.token)}
          confirmed={attachmentsConfirmed}
          onConfirmChange={onAttachmentsConfirmChange}
        />
      )}
      {isBundle && (
        <div
          className="flex flex-wrap gap-1.5 mb-3 border-b border-stone-200 pb-3"
          data-testid="wizard-preview-tabs"
        >
          {mintResult.links.map((L, idx) => (
            <button
              key={L.token}
              type="button"
              onClick={() => setActiveIdx(idx)}
              data-testid={`wizard-preview-tab-${L.entry_number}`}
              className={`text-[11px] font-mono tracking-[0.12em] uppercase px-3 py-1.5 rounded-lg transition-colors ${
                idx === activeIdx
                  ? "bg-[#1B4F8A] text-white"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              Option {idx + 1} · #{L.entry_number}
            </button>
          ))}
        </div>
      )}
      <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 flex items-center gap-2 mb-4">
        <code
          data-testid="wizard-share-url"
          className="flex-1 text-xs sm:text-sm font-mono text-slate-700 truncate select-none pointer-events-none"
          onCopy={(e) => e.preventDefault()}
          onContextMenu={(e) => e.preventDefault()}
          aria-hidden="true"
        >
          {activeLink.shareUrl}
        </code>
      </div>
      <p className="text-sm text-stone-600 leading-relaxed mb-4">
        <span className="font-semibold">Open preview</span> shows the
        proposal as a staff member would see it. The link is not
        gated to your recipients yet, so it is not copyable at this
        stage. The copy and client-view actions unlock on the final
        step once the gate has been locked to specific work-email
        domains.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={previewUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="wizard-preview-link"
          className="inline-flex items-center gap-2 bg-[#1B4F8A] hover:bg-[#163F70] text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open preview
        </a>
        <button
          type="button"
          onClick={onContinue}
          disabled={gateLocked}
          title={
            gateLocked
              ? "Tick the attachment checkbox above (or delete unwanted files) to continue"
              : undefined
          }
          data-testid="wizard-preview-continue"
          className={`ml-auto inline-flex items-center gap-1.5 text-xs font-mono tracking-[0.12em] uppercase px-3 py-2 rounded-md transition-colors ${
            gateLocked
              ? "text-stone-300 cursor-not-allowed"
              : "text-stone-600 hover:text-[#1B4F8A]"
          }`}
        >
          I&apos;ve reviewed it
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function StepConfirm({ onYes, onBack }) {
  return (
    <div>
      <p className="text-sm text-stone-600 leading-relaxed mb-4">
        If anything looks off (wrong client, wrong proposal, missing
        attachment), go back and re-prepare. Otherwise we can move on
        to the email step.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onYes}
          data-testid="wizard-confirm-yes"
          className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          Yes - looks good
        </button>
        <button
          type="button"
          onClick={onBack}
          data-testid="wizard-confirm-back"
          className="inline-flex items-center gap-1.5 bg-white border border-stone-300 hover:border-stone-400 text-slate-700 text-xs font-mono tracking-[0.12em] uppercase px-4 py-2.5 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Not yet
        </button>
      </div>
    </div>
  );
}

function StepNextChoice({ onReply, onJustLink }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <button
        type="button"
        onClick={onReply}
        data-testid="wizard-path-reply"
        className="text-left bg-white border-2 border-stone-200 hover:border-[#1B4F8A] rounded-xl p-5 transition-all group"
      >
        <div className="bg-[#1B4F8A]/10 border border-[#1B4F8A]/20 rounded-lg p-2 inline-block mb-3">
          <Mail className="w-4 h-4 text-[#1B4F8A]" />
        </div>
        <p className="text-sm font-medium text-slate-900 mb-1">
          Reply to a client email
        </p>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Drop the RFP .msg - AI drafts a threaded reply containing
          the link. You can edit before downloading the .eml.
        </p>
      </button>
      <button
        type="button"
        onClick={onJustLink}
        data-testid="wizard-path-just-link"
        className="text-left bg-white border-2 border-stone-200 hover:border-[#1B4F8A] rounded-xl p-5 transition-all group"
      >
        <div className="bg-stone-100 border border-stone-200 rounded-lg p-2 inline-block mb-3">
          <Link2 className="w-4 h-4 text-slate-700" />
        </div>
        <p className="text-sm font-medium text-slate-900 mb-1">
          Just give me the link
        </p>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Copy the URL and send it your own way. Add recipients now
          so the link is gated - or skip and configure on /deals
          later.
        </p>
      </button>
    </div>
  );
}

function StepMsgUpload({ onParse, parsing, parsed, onReset }) {
  const fileInputRef = (typeof window !== "undefined")
    ? { current: null }
    : null;
  return (
    <div>
      <label
        htmlFor="wizard-msg-input"
        data-testid="wizard-msg-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const file = e.dataTransfer.files?.[0];
          if (file) onParse(file);
        }}
        className={`block border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
          parsing
            ? "border-[#1B4F8A]/40 bg-[#1B4F8A]/[0.04]"
            : "border-stone-300 hover:border-[#1B4F8A] hover:bg-stone-50"
        }`}
      >
        <input
          id="wizard-msg-input"
          ref={fileInputRef}
          type="file"
          accept=".msg"
          className="hidden"
          data-testid="wizard-msg-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onParse(file);
            e.target.value = "";
          }}
        />
        {parsing ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin text-[#1B4F8A] mx-auto mb-2" />
            <p className="text-sm text-slate-700">Parsing email…</p>
          </>
        ) : parsed ? (
          <>
            <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
            <p className="text-sm text-slate-700 mb-1">
              <span className="font-mono">{parsed.from_email}</span> parsed
              successfully.
            </p>
            <p className="text-[11px] text-stone-500">
              Drop another <span className="font-mono">.msg</span> here to
              replace it, or click below.
            </p>
          </>
        ) : (
          <>
            <Upload className="w-5 h-5 text-stone-400 mx-auto mb-2" />
            <p className="text-sm text-slate-700">
              Drop the client&apos;s <span className="font-mono">.msg</span> file
              here, or click to browse.
            </p>
            <p className="text-[11px] text-stone-500 mt-1">
              Saved from Outlook · max 5 MB
            </p>
          </>
        )}
      </label>
      {parsed && (
        <div
          data-testid="wizard-msg-parsed"
          className="mt-4 bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs text-slate-700 space-y-1"
        >
          <p>
            <span className="font-mono text-[#1B4F8A]">From:</span>{" "}
            {parsed.from_email}
          </p>
          {parsed.subject && (
            <p>
              <span className="font-mono text-[#1B4F8A]">Subject:</span>{" "}
              {parsed.subject}
            </p>
          )}
          {parsed.suggested_recipients?.length > 0 && (
            <p>
              <span className="font-mono text-[#1B4F8A]">Will reply to:</span>{" "}
              {parsed.suggested_recipients.join(", ")}
            </p>
          )}
        </div>
      )}
      {parsed && typeof onReset === "function" && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onReset}
            data-testid="wizard-msg-reupload-btn"
            className="inline-flex items-center gap-1.5 text-stone-600 hover:text-rose-700 text-[11px] font-mono tracking-[0.12em] uppercase px-3 py-1.5 rounded-md hover:bg-rose-50 transition-colors"
          >
            <RotateCcw className="w-3 h-3" />
            Wrong email? Re-upload
          </button>
        </div>
      )}
    </div>
  );
}

function StepDraft({
  draftBody,
  setDraftBody,
  draftBusy,
  emlBusy,
  resolvedTo,
  resolvedCc,
  alwaysCc,
  onResetEmail,
  onConfirm,
}) {
  if (draftBusy) {
    return (
      <div className="text-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-[#1B4F8A] mx-auto mb-3" />
        <p className="text-sm text-stone-600">
          Claude is drafting your reply…
        </p>
        <p className="text-xs text-stone-400 mt-1.5">
          Long email threads can take a minute or two - the draft is
          being written in the background, no need to refresh.
        </p>
      </div>
    );
  }
  const alwaysSet = new Set(
    (alwaysCc || []).map((e) => (e || "").trim().toLowerCase()),
  );
  return (
    <div>
      {/* Recipients confirmation - the SAME list the backend will put
          on the .eml. Always-CC entries (Frank / Nicki / Arooj) are
          tagged so staff can see at a glance which addresses are
          fixed vs carried over from the original email's CC line. */}
      {(resolvedTo?.length > 0 || resolvedCc?.length > 0) && (
        <div
          data-testid="wizard-recipients-confirmation"
          className="mb-4 bg-stone-50/80 border border-stone-200 rounded-xl p-3 sm:p-4 text-xs"
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#1B4F8A] flex items-center gap-1.5">
              <Send className="w-3 h-3" />
              This reply will be sent to
            </p>
            {typeof onResetEmail === "function" && (
              <button
                type="button"
                onClick={onResetEmail}
                data-testid="wizard-draft-reupload-btn"
                className="inline-flex items-center gap-1 text-[10px] font-mono tracking-[0.12em] uppercase text-stone-500 hover:text-rose-700 px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Wrong email? Re-upload
              </button>
            )}
          </div>
          {resolvedTo?.length > 0 && (
            <div className="flex items-start gap-2 mb-1.5">
              <span className="font-mono text-stone-400 w-6 shrink-0">To</span>
              <span
                data-testid="wizard-recipient-to"
                className="font-mono text-slate-800 break-all"
              >
                {resolvedTo[0].name
                  ? `${resolvedTo[0].name} <${resolvedTo[0].email}>`
                  : resolvedTo[0].email}
              </span>
            </div>
          )}
          {resolvedCc?.length > 0 && (
            <div className="flex items-start gap-2">
              <span className="font-mono text-stone-400 w-6 shrink-0">Cc</span>
              <ul className="flex flex-wrap gap-1.5 flex-1">
                {resolvedCc.map((c) => {
                  const isAlways = alwaysSet.has(
                    (c.email || "").trim().toLowerCase(),
                  );
                  return (
                    <li
                      key={c.email}
                      data-testid={`wizard-recipient-cc-${c.email}`}
                      className={`inline-flex items-center gap-1 font-mono px-1.5 py-0.5 rounded text-[11px] border ${
                        isAlways
                          ? "bg-[#1B4F8A]/[0.06] border-[#1B4F8A]/20 text-[#1B4F8A]"
                          : "bg-white border-stone-200 text-slate-700"
                      }`}
                      title={isAlways ? "Medicys team (always copied)" : "Carried over from the client's email"}
                    >
                      {c.email}
                      {isAlways && (
                        <span className="text-[9px] uppercase tracking-wider opacity-70">
                          team
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {alwaysCc?.length > 0 && (
            <p className="text-[10px] text-stone-500 mt-2 italic">
              Frank, Nicki and Arooj are always copied - they ensure
              continuity across the team.
            </p>
          )}
        </div>
      )}

      <p className="text-sm text-stone-600 leading-relaxed mb-3">
        Tweak the wording below so it sounds like you. The downloaded
        .eml will use this text verbatim - threading + signature are
        added automatically.
      </p>
      <textarea
        value={draftBody}
        onChange={(e) => setDraftBody(e.target.value)}
        rows={12}
        data-testid="wizard-draft-textarea"
        className="w-full text-sm font-mono bg-white border border-stone-300 focus:border-[#3EA8DC] focus:outline-none focus:ring-1 focus:ring-[#3EA8DC]/30 rounded-xl p-4 leading-relaxed text-slate-800 resize-y"
      />
      <p className="text-[11px] text-stone-500 mt-2">
        {draftBody.length} characters · the share URL is already woven
        into the body
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onConfirm}
          disabled={emlBusy || !draftBody.trim()}
          data-testid="wizard-build-eml-btn"
          className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-progress text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          {emlBusy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FileDown className="w-3.5 h-3.5" />
          )}
          {emlBusy ? "Building…" : "Looks good - build .eml"}
        </button>
      </div>
    </div>
  );
}

function StepEmlDone({
  emlFilename,
  onRestart,
}) {
  // The .eml downloads automatically the moment it is built (see
  // `onBuildEml` above - a synthetic anchor click fires as soon as
  // the blob is ready). Staff see the file appear in their browser's
  // download bar / Downloads folder without touching anything, so
  // the explicit "Open in Outlook" and "Save .eml file" buttons are
  // redundant noise. Removed on user request. The .eml carries the
  // ``X-Unsent: 1`` header so double-clicking it launches Outlook in
  // compose mode (editable draft with a Send button, HTML signature,
  // clickable proposal label, full CC list, threaded headers).
  //
  // The shareable proposal URL that landed in the .eml also sits on
  // this step (as a monospace read-only bar). We block selection /
  // right-click / copy on it so staff can't inadvertently share the
  // link out-of-band from this step - the .eml already carries the
  // link inside the draft, and the "Just give me the link" branch
  // has the proper Copy button with the hyperlink label anyway.
  return (
    <div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
        <p className="text-sm text-emerald-900 leading-relaxed flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Reply <span className="font-mono">.eml</span> downloaded
            (look for{" "}
            <span className="font-mono">{emlFilename || "your reply"}</span>{" "}
            in your Downloads folder or your browser&apos;s download
            bar). Double-click it to launch Outlook in compose mode:
            polished HTML draft, signature, clickable proposal link,
            full CC list, and the original thread quoted underneath,
            ready for you to review and send.
          </span>
        </p>
      </div>

      <button
        type="button"
        onClick={onRestart}
        data-testid="wizard-restart-btn"
        className="inline-flex items-center gap-1.5 bg-white border border-stone-300 hover:border-stone-400 text-slate-700 text-xs font-mono tracking-[0.12em] uppercase px-4 py-2.5 rounded-lg transition-colors"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        Start a new mint
      </button>
    </div>
  );
}

function StepRecipients({
  recipients,
  setRecipients,
  draft,
  setDraft,
  add,
  busy,
  onSave,
  onSkip,
}) {
  const onKey = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        if (add(draft)) setDraft("");
      }
    } else if (e.key === "Backspace" && !draft && recipients.length) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };
  return (
    <div>
      <p className="text-sm text-stone-600 leading-relaxed mb-4">
        Add the work emails you&apos;re sending this link to. We&apos;ll lock
        the link to those domains so it can&apos;t be forwarded to anyone
        else.
      </p>
      <div
        className="flex flex-wrap items-center gap-2 min-h-[44px] px-2 py-1.5 border border-stone-300 rounded-lg bg-white focus-within:border-[#3EA8DC] transition-colors"
        onClick={(e) => {
          const input = e.currentTarget.querySelector("input");
          input?.focus();
        }}
      >
        {recipients.map((email) => (
          <span
            key={email}
            data-testid={`wizard-recipient-chip-${email}`}
            className="inline-flex items-center gap-1.5 bg-[#1B4F8A]/8 text-[#1B4F8A] text-xs font-mono px-2 py-1 rounded-md border border-[#1B4F8A]/20"
          >
            {email}
            <button
              type="button"
              onClick={() =>
                setRecipients((prev) => prev.filter((e) => e !== email))
              }
              className="hover:text-rose-600 transition-colors"
              aria-label={`Remove ${email}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="email"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => {
            if (draft.trim() && add(draft)) setDraft("");
          }}
          placeholder={
            recipients.length === 0
              ? "name@client.com - press Enter to add"
              : "Add another…"
          }
          data-testid="wizard-recipient-input"
          className="flex-1 min-w-[180px] bg-transparent text-sm text-slate-900 placeholder:text-stone-400 focus:outline-none py-1"
        />
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onSkip}
          data-testid="wizard-recipients-skip"
          className="inline-flex items-center gap-1.5 bg-white border border-stone-300 hover:border-stone-400 text-slate-700 text-xs font-mono tracking-[0.12em] uppercase px-4 py-2.5 rounded-lg transition-colors"
        >
          Skip - configure later
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy || recipients.length === 0}
          data-testid="wizard-recipients-save"
          className="inline-flex items-center gap-1.5 bg-[#1B4F8A] hover:bg-[#163F70] disabled:opacity-40 text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Plus className="w-3.5 h-3.5" />
          )}
          {busy ? "Saving…" : "Lock link to recipients"}
        </button>
      </div>
    </div>
  );
}

function StepLinkDone({
  mintResult,
  recipientCount,
  recipientEmails = [],
  onRestart,
}) {
  // Compute the effective allow-list. As of iteration 42 the gate
  // matches on EXACT recipient emails (see share_gate.validate_email_against_link):
  // ``recipient_emails`` is authoritative + Medicys is always allowed.
  // We render the exact addresses so staff can visually confirm the
  // gate scope. Extra_allowed_domains (wildcards) are added on top
  // only when staff has explicitly configured them for this link.
  const allowedEmails = Array.isArray(recipientEmails) ? recipientEmails : [];
  const extraDomains =
    (Array.isArray(mintResult?.extra_allowed_domains)
      ? mintResult.extra_allowed_domains
      : []) || [];

  const linkLabel = buildProposalLinkLabel(mintResult);
  const handleCopy = () => copyProposalLinkAsHyperlink(mintResult);

  return (
    <div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
        <p className="text-sm text-emerald-900 leading-relaxed flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {recipientCount > 0
              ? `Link is live and gated to ${recipientCount} recipient${
                  recipientCount === 1 ? "" : "s"
                }. Copy below and send via your preferred channel.`
              : "Link is minted but still locked (no recipients yet). Configure recipients on the deal detail panel before sending."}
          </span>
        </p>
        {recipientCount > 0 && allowedEmails.length > 0 && (
          <div className="mt-3 pt-3 border-t border-emerald-200/60">
            <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-emerald-800/80 mb-2">
              Addresses that can open this link
            </p>
            <div
              className="flex flex-wrap gap-1.5"
              data-testid="wizard-allowed-emails"
            >
              {allowedEmails.map((em) => (
                <span
                  key={em}
                  className="inline-flex items-center gap-1 bg-white/70 text-emerald-900 text-xs font-mono px-2 py-1 rounded-md border border-emerald-200"
                  data-testid={`wizard-allowed-email-${em}`}
                >
                  {em}
                </span>
              ))}
              <span
                className="inline-flex items-center gap-1 bg-white/70 text-emerald-900 text-xs font-mono px-2 py-1 rounded-md border border-emerald-200"
                data-testid="wizard-allowed-medicys"
                title="Medicys staff can always open any link they have the URL for"
              >
                <span>@medicysltd.co.uk</span>
                <span className="text-[9px] uppercase tracking-wider text-emerald-700/70">
                  always
                </span>
              </span>
              {extraDomains.map((dom) => (
                <span
                  key={`wild-${dom}`}
                  className="inline-flex items-center gap-1 bg-amber-50 text-amber-900 text-xs font-mono px-2 py-1 rounded-md border border-amber-200"
                  data-testid={`wizard-allowed-wildcard-${dom}`}
                  title="Whole-domain wildcard: anyone at this domain can open the link"
                >
                  <span>@{dom}</span>
                  <span className="text-[9px] uppercase tracking-wider text-amber-700/80">
                    wildcard
                  </span>
                </span>
              ))}
            </div>
            <p className="text-[11px] text-emerald-900/70 mt-2 leading-relaxed">
              Only these exact addresses pass the gate. Personal domains
              (gmail, outlook, etc.) are always rejected.{" "}
              <span className="font-semibold">@medicysltd.co.uk</span>{" "}
              is added automatically so staff are never locked out. Clients
              can forward the link to same-domain colleagues from inside the
              viewer.
            </p>
          </div>
        )}
      </div>
      {/* Iter-43: bundle-download UI when the wizard minted multiple
          options. Shows an "Download .eml (HTML)" button that fetches
          the pre-drafted multi-option cover email with real hyperlinks
          per option. Falls back gracefully - staff can still copy each
          link individually via the chip strip below. */}
      {Array.isArray(mintResult?.links) && mintResult.links.length > 1 && (
        <BundleDoneStrip
          bundleId={mintResult.bundle_id}
          links={mintResult.links}
        />
      )}
      {/* Show the rendered label first (this is what will paste into
          Outlook / Word) so staff know exactly what the recipient
          will see. The raw URL sits underneath as a monospace
          reference. */}
      <div className="bg-white border border-stone-200 rounded-xl p-4 mb-2">
        <div
          className="text-sm font-semibold text-[#1B4F8A]"
          data-testid="wizard-link-label"
        >
          {linkLabel}
        </div>
        <code
          className="block mt-1 text-xs font-mono text-slate-500 truncate"
          data-testid="wizard-link-url"
        >
          {mintResult.shareUrl}
        </code>
      </div>
      <p className="text-[11px] text-stone-500 italic mb-4">
        <span className="font-semibold">Copy link</span> puts a
        clickable hyperlink on your clipboard - paste into Outlook or
        Word and you&apos;ll see &ldquo;{linkLabel}&rdquo; as a proper
        blue underlined link. Plain-text apps (Slack, Notepad) get{" "}
        <span className="font-mono">{linkLabel}: &lt;url&gt;</span>{" "}
        instead.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleCopy}
          data-testid="wizard-link-done-copy"
          className="inline-flex items-center gap-1.5 bg-white border border-stone-300 hover:border-stone-400 text-slate-700 text-xs font-mono tracking-[0.12em] uppercase px-4 py-2.5 rounded-lg transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
          Copy link
        </button>
        <a
          href={
            mintResult.shareUrl.includes("?")
              ? `${mintResult.shareUrl}&as=client`
              : `${mintResult.shareUrl}?as=client`
          }
          target="_blank"
          rel="noopener noreferrer"
          data-testid="wizard-link-done-open"
          title="Opens the link as a client would see it - your staff bypass is disabled so you'll be asked to enter a work email at the gate. Add or remove ?as=client from the URL to toggle."
          className="inline-flex items-center gap-1.5 bg-[#1B4F8A] hover:bg-[#163F70] text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open as client
        </a>
        <button
          type="button"
          onClick={onRestart}
          data-testid="wizard-link-done-restart"
          className="ml-auto inline-flex items-center gap-1.5 text-stone-600 hover:text-[#1B4F8A] text-xs font-mono tracking-[0.12em] uppercase px-3 py-2 rounded-md transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Start a new mint
        </button>
      </div>
    </div>
  );
}


function BundleDoneStrip({ bundleId, links }) {
  const [drafting, setDrafting] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftPreview, setDraftPreview] = useState(null);

  // Kick the draft-email endpoint so the .eml is warm when staff
  // clicks Download. Also gives us a text preview for the "See
  // preview" affordance below.
  const prepareDraft = async () => {
    if (drafting || draftReady) return;
    setDrafting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/proposals/share/bundle/${bundleId}/draft-email`,
        {},
      );
      setDraftPreview(data);
      setDraftReady(true);
    } catch (err) {
      toast.error(renderApiError(err, "Draft failed."));
    } finally {
      setDrafting(false);
    }
  };

  const downloadEml = async () => {
    // Draft first if we haven't - the .eml endpoint auto-runs the
    // drafter but a warm cache means Outlook opens instantly.
    if (!draftReady) await prepareDraft();
    // Trigger the browser download. Using an <a download> hidden
    // click keeps the current tab alive.
    const url = `${API}/api/proposals/share/bundle/${bundleId}/bundle.eml`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `medicys-bundle-${bundleId}.eml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copyAllLinks = async () => {
    // Rich clipboard: HTML with clickable labelled anchors + plain
    // text fallback (angle-bracketed URLs).
    const htmlParts = ["<p>Medicys proposal options:</p><ol>"];
    const textParts = ["Medicys proposal options:", ""];
    links.forEach((L, i) => {
      const label = `${L.project_reference || "Proposal"} (${L.entry_number})`;
      htmlParts.push(
        `<li><a href="${L.shareUrl}">${label}</a></li>`,
      );
      textParts.push(`  ${i + 1}. ${label}: <${L.shareUrl}>`);
    });
    htmlParts.push("</ol>");
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlParts.join("")], { type: "text/html" }),
          "text/plain": new Blob([textParts.join("\n")], { type: "text/plain" }),
        }),
      ]);
      toast.success(`${links.length} links copied.`);
    } catch {
      try {
        await navigator.clipboard.writeText(textParts.join("\n"));
        toast.success(`${links.length} links copied.`);
      } catch {
        toast.error("Clipboard blocked - copy manually.");
      }
    }
  };

  return (
    <div
      className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4"
      data-testid="wizard-bundle-done-strip"
    >
      <p className="text-sm text-teal-900 leading-relaxed mb-3 flex items-start gap-2">
        <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Bundle of <b>{links.length}</b> options minted. Download the
          cover email as an <span className="font-mono">.eml</span>{" "}
          file, or copy all links as a rich hyperlink list.
        </span>
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={downloadEml}
          disabled={drafting}
          data-testid="wizard-bundle-download-eml"
          className="inline-flex items-center gap-1.5 bg-[#0d9488] hover:bg-[#0f766e] disabled:opacity-50 disabled:cursor-progress text-white text-xs font-mono tracking-[0.12em] uppercase font-semibold px-4 py-2.5 rounded-lg transition-colors"
        >
          {drafting ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5" />
          )}
          {drafting ? "Drafting…" : "Download cover email (.eml)"}
        </button>
        <button
          type="button"
          onClick={copyAllLinks}
          data-testid="wizard-bundle-copy-all"
          className="inline-flex items-center gap-1.5 bg-white border border-teal-300 hover:border-teal-500 text-teal-900 text-xs font-mono tracking-[0.12em] uppercase px-4 py-2.5 rounded-lg transition-colors"
        >
          <Copy className="w-3.5 h-3.5" />
          Copy all links
        </button>
        <button
          type="button"
          onClick={prepareDraft}
          disabled={drafting || draftReady}
          data-testid="wizard-bundle-preview-email"
          className="inline-flex items-center gap-1.5 text-teal-800 hover:text-teal-900 text-xs font-mono tracking-[0.12em] uppercase px-3 py-2 rounded-md transition-colors disabled:opacity-40"
        >
          {draftReady ? "Preview ready below" : "Preview email"}
        </button>
      </div>
      {draftReady && draftPreview && (
        <div className="mt-3 pt-3 border-t border-teal-200/60">
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-teal-800/80 mb-2">
            Draft preview (plain text)
          </p>
          <pre
            className="text-[11px] whitespace-pre-wrap font-mono text-slate-800 bg-white/70 border border-teal-200 rounded-md p-3 max-h-64 overflow-y-auto"
            data-testid="wizard-bundle-preview-text"
          >
            {draftPreview.text}
          </pre>
        </div>
      )}
    </div>
  );
}
